import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { IContentItem } from "@kontent-ai/delivery-sdk"

import type { Button as SDKButton } from "@/types/kontent-generated/types/button.generated"
import type { TextMediaGroup } from "@/types/kontent-generated/types/text-media-group.generated"
import type { TextMedia } from "@/types/kontent-generated/types/text-media.generated"
import { getDeliveryClient } from "@/lib/kontent/client"
import {
  _mapTextMediaItem,
  TResolvedTextMedia,
} from "@/lib/kontent/components/getTextMedia"
import { getWebSpotlightApplicationTree } from "@/lib/kontent/config/getApplicationTree"
import { TButton } from "@/components/ui/atoms"

import { assertItemResponse, fetchKontent } from "../helpers"
import { mapButtonToTButton } from "../mappers"
import { resolveRichText } from "../richText"

export type TResolvedTextMediaGroup = {
  title: string
  textBlocks: TResolvedTextMedia[]
  headerBackgroundColor: string
  button: TButton | undefined
  smallInfo: string
}

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function GetTextMediaGroupFromItem(
  rawItem: IContentItem,
  previewMode = false
): Promise<TResolvedTextMediaGroup> {
  const item = rawItem as TextMediaGroup

  const textBlocks = await Promise.all(
    (item.elements.text_blocks.linkedItems as TextMedia[]).map(
      async (textMediaItem) => {
        const resolved = _mapTextMediaItem(textMediaItem)
        resolved.text = await resolveRichText(resolved.text, previewMode)
        return resolved
      }
    )
  )

  const buttonItem = item.elements.button.linkedItems[0] as
    | SDKButton
    | undefined
  let button: TButton | undefined
  if (buttonItem) {
    const { byCodename } = await getWebSpotlightApplicationTree(previewMode)
    button = mapButtonToTButton(buttonItem, byCodename)
  }

  return {
    title: item.elements.title.value,
    textBlocks,
    headerBackgroundColor:
      item.elements.header_background_color.value[0]?.name ?? "",
    button,
    smallInfo: item.elements.small_info.value,
  }
}

async function _fetchTextMediaGroup(
  codename: string,
  previewMode = false
): Promise<TResolvedTextMediaGroup> {
  return fetchKontent("TextMediaGroup", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<TextMediaGroup>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(3)
      .toPromise()

    const item = assertItemResponse(response, "TextMediaGroup")
    return GetTextMediaGroupFromItem(item, previewMode)
  })
}

export const GetTextMediaGroup = cache(_fetchTextMediaGroup)
